import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ITicket, ITicketUpdateFormData } from '@/interfaces/tickets/ITicket';
import { ticketsService } from '@/services/tickets.service';
import { notificationService } from '@/services/notification.service';
import { TicketForm } from './components/TicketForm';
import { Modal } from '@/components/common/Modal';

export const EditTicket = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<ITicket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (uuid) loadTicket(uuid);
  }, [uuid]);

  const loadTicket = async (ticketUuid: string) => {
    try {
      const response = await ticketsService.getTicketById(ticketUuid);
      const { attributes, uuid } = response.data;
      setTicket({
        ...attributes,
        uuid
      });
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error loading ticket');
      }
      navigate('/tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (formData: ITicketUpdateFormData) => {
    if (!uuid || !ticket) return;

    const changedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (key === 'file') {
        if (value?.length) {
          acc[key] = value;
        }
        return acc;
      }

      if (value !== ticket[key as keyof ITicket]) {
        acc[key as keyof ITicketUpdateFormData] = value;
      }
      return acc;
    }, {} as Partial<ITicketUpdateFormData>);

    if (Object.keys(changedData).length === 0) {
      notificationService.error('No changes detected');
      return;
    }

    setIsUpdating(true);
    try {
      await ticketsService.updateTicket(uuid, changedData);
      notificationService.success('Ticket updated successfully');
      navigate(`/tickets/${uuid}`);
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error updating ticket');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!uuid) return;

    setIsDeleting(true);
    try {
      await ticketsService.deleteTicket(uuid);
      notificationService.success('Ticket deleted successfully');
      navigate('/tickets');
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error deleting ticket');
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64 font-body">Loading ticket...</div>;
  }

  if (!ticket) {
    return <div className="text-center py-8 font-body">Ticket not found</div>;
  }

  const ticketFormData = {
    ...ticket,
    file: undefined
  };

  return (
    <>
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Ticket"
      >
        <div className="p-4">
          <p className="mb-4 font-body">Are you sure you want to delete this ticket? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowDeleteModal(false)}
              data-testid="cancel-delete-button"
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              data-testid="confirm-delete-button"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-[#0F9015] text-4xl font-heading">Edit Ticket</h1>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/tickets/${uuid}`)}
                className="bg-[#C9FD05] hover:opacity-90 transition-opacity px-6 py-2 rounded-lg text-lg font-body"
              >
                Back to Details
              </button>
              <button
                onClick={handleDelete}
                data-testid="delete-ticket-button"
                className="bg-red-600 text-white hover:bg-red-700 transition-colors px-6 py-2 rounded-lg text-lg font-body flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Ticket
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-8">
            <TicketForm
              onSubmit={handleUpdate}
              isLoading={isUpdating}
              initialData={ticketFormData}
            />
          </div>
        </div>
      </div>
    </>
  );
}; 